import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import {
  adminMenuAuthMatrixEndPoints,
  adminMenuInviteUserEndPoints,
} from "../urlConfig/apiEndpoints";
import type { ApiResponse } from "./ApiResponse";
import {
  extractErrorMessage,
  showError,
  showSuccess,
} from "../errorservices/errorservices";
import { getToken } from "@/app/services/context/tokenGetter";
import type {
  AuthMatrixGroupCreatePayload,
  AuthMatrixGroupDeletePayload,
  AuthMatrixPermissionPayload,
  EmailVerifyErrorResponse,
  GroupDeleteValidateResponse,
  SelfOnboardUsingDefaultsPayload,
} from "@/app/types/adminMenu";
import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/app/utils/loginFlow/correlationId";

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

// ---------------- Generic API handler ----------------
const handleApiRequest = async <T>(
  method: typeof axios.get | typeof axios.post | typeof axios.delete,
  url: string,
  payload: any = {},
  showSuccessMsg?: boolean,
): Promise<ApiResponse<T>> => {
  try {
    const correlationId = generateCorrelationId();
    const token = getToken();
    const tenantId = getTenantId();
    const headers: Record<string, string | undefined> = {
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
      appId: tenantId,
      [CORRELATION_ID_HEADER]: correlationId,
    };
    if (!(payload instanceof FormData))
      headers["Content-Type"] = "application/json";

    const config: AxiosRequestConfig = { headers };
    const response: AxiosResponse =
      method === axios.get || method === axios.delete
        ? await method(url, { ...config, params: payload })
        : await method(url, payload, config);

    const data: ApiResponse<T> = response.data;

    if (data?.status === 0) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 1) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 2) {
      showError(undefined, data.errorMessage || "Request failed.");
      throw new Error(data.errorMessage || "Request failed.");
    }

    return data;
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    showError(code, message);
    return Promise.reject(err);
  }
};

// ---------------- Auth Matrix APIs ----------------
export const gxAuthMatrixListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixUserIdSummary,
    payload,
    false,
  );

export const gxPaymentTypeViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixPaymenttypeId,
    payload,
    false,
  );

export const gxAuthMatrixCreateApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixCreate,
    payload,
    false,
  );

export const gxAuthMatrixUpdateApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixUpdate,
    payload,
    false,
  );

export const gxAuthMatrixDeleteApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixDelete,
    payload,
    false,
  );

// ---------------- Invite User APIs ----------------
export const gxInviteUserViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteUserView,
    payload,
    false,
  );

export const gxInviteUserFeaturesDtoViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteUserfeaturesDtoView,
    payload,
    false,
  );

export const gxInviteResendInviteApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteResendInvite,
    payload,
    false,
  );

export const gxInviteListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteList,
    payload,
    false,
  );

export const gxInviteUserDeleteApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteDelete,
    payload,
    false,
  );

export const gxInviteUserCreateApi = (
  payload: SelfOnboardUsingDefaultsPayload,
) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteCreate,
    payload,
    false,
  );

export const gxInviteUserEmailVerifyApi = (payload: any) =>
  handleApiRequest<EmailVerifyErrorResponse>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteEmailVerify,
    payload,
    false,
  );

export const gxInviteUsingDefaultsApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteDefault,
    payload,
    false,
  );

export const gxInviteUsingCountryListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteCountryList,
    payload,
    false,
  );

export const gxInviteWorkflowViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteWorkflowView,
    payload,
    false,
  );

export const gxInviteFeaturesViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteFeaturesView,
    payload,
    false,
  );

export const gxInviteAccountViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxInviteAccountView,
    payload,
    false,
  );

export const gxSmeUserBankListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxSmeUserBankList,
    payload,
    false,
  );

export const gxSmeUserInviteBankListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuInviteUserEndPoints.gxSmeUserInviteBankList,
    payload,
    false,
  );

export const gxAuthMatrixViewSummaryApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixViewSummary,
    payload,
    false,
  );

export const gxAuthMatrixAccountListApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixAccountsList,
    payload,
    false,
  );

export const gxAuthMatrixViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixView,
    payload,
    false,
  );

export const gxAuthMatrixGroupCreateApi = (
  payload: AuthMatrixGroupCreatePayload[],
) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixGroupCreate,
    payload,
    false,
  );

export const gxAuthMatrixGroupViewApi = (payload: any) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixGroupView,
    payload,
    false,
  );

export const gxAuthMatrixGroupDeleteApi = (
  payload: AuthMatrixGroupDeletePayload,
) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixGroupDelete,
    payload,
    false,
  );

export const gxAuthMatrixPermissionValidateApi = (
  payload: AuthMatrixPermissionPayload,
) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixPermissionValidate,
    payload,
    false,
  );

export const gxAuthMatrixRolePermissionValidateApi = (payload: {
  id: number;
}) =>
  handleApiRequest<any>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixRolePermissionValidate,
    payload,
    false,
  );

export const gxAuthMatrixGroupValidateDelete = (payload: { id: number }) =>
  handleApiRequest<GroupDeleteValidateResponse>(
    axios.post,
    adminMenuAuthMatrixEndPoints.gxAuthMatrixGroupValidateDelete,
    payload,
    false,
  );
